import mapboxgl, { CameraOptions, LngLatBoundsLike } from 'mapbox-gl';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { Line, Marker } from './types';
import { addLineToMap, getMarkerElement } from './utils';
import { useTripExtra } from '../../services/trip-extra/useTripExtra';
import { MapButton } from '../button/MapButton';
import { LocationCrosshairsIcon } from '../icon/LocationCrosshairsIcon';

type Props = {
  markers?: Marker[];
  lines?: Line[];
  bounds?: LngLatBoundsLike;
  children?: ReactNode;
};

export const Map = ({ markers, lines, bounds, children }: Props) => {
  const [isCentered, setIsCentered] = useState(true);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map>();
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const { setAnimateCamera } = useTripExtra();

  const animateCamera = useCallback((camera: CameraOptions) => {
    if (map.current) {
      map.current.flyTo(camera);
      setIsCentered(false);
    }
  }, []);

  const resetCamera = useCallback(() => {
    if (map.current && bounds) {
      map.current.fitBounds(bounds, {
        duration: 500,
        padding: { bottom: 42, left: 42, right: 42, top: 42 },
      });
      setIsCentered(true);
    }
  }, [bounds]);

  // Init map
  useEffect(() => {
    if (map.current || !mapContainer.current) return;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
    });
    map.current.on('load', () => setIsMapLoaded(true));
    map.current.on('dragstart', () => setIsCentered(false));
    map.current.on('touchstart', () => setIsCentered(false));
    map.current.on('zoomstart', () => setIsCentered(false));
  }, []);

  // Draw markers
  useEffect(() => {
    if (map.current && markers && isMapLoaded) {
      const mapboxMarkers = markers.map(marker =>
        new mapboxgl.Marker({ element: getMarkerElement(marker.type) }).setLngLat({
          lat: marker.lat,
          lng: marker.lng,
        }),
      );

      for (const marker of mapboxMarkers) {
        marker.addTo(map.current);
      }

      return () => {
        for (const marker of mapboxMarkers) {
          marker.remove();
        }
      };
    }
  }, [isMapLoaded, markers]);

  useEffect(() => {
    if (map.current && lines && isMapLoaded) {
      for (const line of lines) {
        addLineToMap(line, map.current);
      }

      return () => {
        for (const line of lines) {
          if (map.current) {
            map.current.removeLayer(line.id);
            map.current.removeSource(line.id);
          }
        }
      };
    }
  }, [isMapLoaded, lines]);

  // Fit map to markers
  useEffect(() => {
    resetCamera();
  }, [resetCamera]);

  useEffect(() => {
    setAnimateCamera(() => animateCamera);
  }, [animateCamera, setAnimateCamera]);

  return (
    <div className="absolute top-0 right-0 bottom-0 left-0">
      <div ref={mapContainer} className="w-full h-full"></div>
      {!isCentered ? (
        <MapButton className="absolute bottom-8 right-4" onClick={() => resetCamera()}>
          <LocationCrosshairsIcon />
        </MapButton>
      ) : null}
      {children}
    </div>
  );
};
